import { analyseIonicEquation, type IonicSpecies } from './ionicEquation';

export interface OxidationAssignment {
  species: IonicSpecies;
  states: Record<string, number>;
  complete: boolean;
}

export interface RedoxChange {
  element: string;
  from: number;
  to: number;
  kind: 'oxidation' | 'reduction';
  electrons: number;
}

export interface RedoxAnalysis {
  atomsBalanced: boolean;
  chargeBalanced: boolean;
  assignments: { left: OxidationAssignment[]; right: OxidationAssignment[] };
  changes: RedoxChange[];
  electronsLost: number;
  electronsGained: number;
  electronTransferBalanced: boolean;
  isRedox: boolean;
  unsupportedSpecies: string[];
  /**
   * Elements with more than one oxidation number on one side, as in disproportionation
   * (Cl₂ → Cl⁻ + ClO⁻) or comproportionation (Br⁻ + BrO₃⁻ → Br₂). The model does not resolve them,
   * so no redox verdict is given while this list is non-empty.
   */
  mixedStateElements: string[];
}

const GROUP_ONE = new Set(['Li', 'Na', 'K', 'Rb', 'Cs', 'Fr']);
const GROUP_TWO = new Set(['Be', 'Mg', 'Ca', 'Sr', 'Ba', 'Ra']);
const EXCEPTION_FORMULAS = new Set([
  'H2O2',
  'Li2O2',
  'Na2O2',
  'K2O2',
  'BaO2',
  'LiH',
  'NaH',
  'KH',
  'CaH2',
]);

/** An ammonium group plus another N-containing group, e.g. NH4NO3: N is −3 and +5, never the average. */
function hasMixedNitrogenSites(formula: string): boolean {
  if (!formula.includes('NH4')) return false;
  const rest = formula.replace(/\(NH4\)\d*|NH4/g, '');
  return /N(?![a-z])/.test(rest);
}

export function usualState(symbol: string): number | null {
  if (GROUP_ONE.has(symbol)) return 1;
  if (GROUP_TWO.has(symbol)) return 2;
  if (symbol === 'F') return -1;
  if (symbol === 'O') return -2;
  if (symbol === 'H') return 1;
  return null;
}

export function assignOxidationStates(species: IonicSpecies): OxidationAssignment {
  if (EXCEPTION_FORMULAS.has(species.formula) || hasMixedNitrogenSites(species.formula)) {
    return { species, states: {}, complete: false };
  }
  const symbols = Object.keys(species.atoms);
  if (symbols.length === 1) {
    const symbol = symbols[0];
    return {
      species,
      states: { [symbol]: species.charge / species.atoms[symbol] },
      complete: true,
    };
  }

  const states: Record<string, number> = {};
  const unknown: string[] = [];
  symbols.forEach((symbol) => {
    const fixed = usualState(symbol);
    if (fixed === null) unknown.push(symbol);
    else states[symbol] = fixed;
  });
  if (unknown.length === 1) {
    const knownTotal = Object.entries(states).reduce(
      (total, [symbol, state]) => total + state * species.atoms[symbol],
      0,
    );
    const symbol = unknown[0];
    states[symbol] = (species.charge - knownTotal) / species.atoms[symbol];
    return { species, states, complete: Number.isInteger(states[symbol]) };
  }
  const assignedTotal = Object.entries(states).reduce(
    (total, [symbol, state]) => total + state * species.atoms[symbol],
    0,
  );
  return {
    species,
    states,
    complete: unknown.length === 0 && assignedTotal === species.charge,
  };
}

function sideStates(assignments: OxidationAssignment[]): Map<string, Set<number>> {
  const result = new Map<string, Set<number>>();
  assignments.forEach((assignment) => {
    Object.entries(assignment.states).forEach(([symbol, state]) => {
      const values = result.get(symbol) ?? new Set<number>();
      values.add(state);
      result.set(symbol, values);
    });
  });
  return result;
}

function elementCount(species: IonicSpecies[], element: string): number {
  return species.reduce((total, item) => total + (item.atoms[element] ?? 0) * item.coefficient, 0);
}

export function analyseRedoxEquation(input: string): {
  value: RedoxAnalysis | null;
  error: string | null;
} {
  const ionic = analyseIonicEquation(input);
  if (!ionic.analysis || ionic.error) {
    return { value: null, error: ionic.error?.message ?? 'Enter a complete equation.' };
  }
  const left = ionic.analysis.equation.left.map(assignOxidationStates);
  const right = ionic.analysis.equation.right.map(assignOxidationStates);
  const leftStates = sideStates(left);
  const rightStates = sideStates(right);
  const changes: RedoxChange[] = [];
  const mixedStateElements = [...new Set([...leftStates, ...rightStates]
    .filter(([, values]) => values.size > 1)
    .map(([element]) => element))];
  for (const [element, fromValues] of leftStates) {
    const toValues = rightStates.get(element);
    if (!toValues || fromValues.size !== 1 || toValues.size !== 1) continue;
    const from = [...fromValues][0];
    const to = [...toValues][0];
    if (from === to) continue;
    changes.push({
      element,
      from,
      to,
      kind: to > from ? 'oxidation' : 'reduction',
      electrons: Math.abs(to - from) * elementCount(ionic.analysis.equation.left, element),
    });
  }
  const electronsLost = changes
    .filter((change) => change.kind === 'oxidation')
    .reduce((total, change) => total + change.electrons, 0);
  const electronsGained = changes
    .filter((change) => change.kind === 'reduction')
    .reduce((total, change) => total + change.electrons, 0);
  const unsupportedSpecies = [...left, ...right]
    .filter((assignment) => !assignment.complete)
    .map((assignment) => assignment.species.formula);
  return {
    value: {
      atomsBalanced: ionic.analysis.atomsBalanced,
      chargeBalanced: ionic.analysis.chargeBalanced,
      assignments: { left, right },
      changes,
      electronsLost,
      electronsGained,
      electronTransferBalanced: electronsLost > 0 && electronsLost === electronsGained,
      isRedox:
        mixedStateElements.length === 0 &&
        changes.some((change) => change.kind === 'oxidation') &&
        changes.some((change) => change.kind === 'reduction'),
      unsupportedSpecies: [...new Set(unsupportedSpecies)],
      mixedStateElements,
    },
    error: null,
  };
}
